// Apolo IDE — Auto-review classifier (S4 · E3-WS1-T02/T03).
//
// Runs BEFORE every tool call (E4 wires it into agentLoop) on the SAME RPC
// stream — the default path is SYNCHRONOUS + deterministic (no extra round-trip,
// risk #4). An optional small-model classifier (opts.classify) can plug in for
// ambiguous cases and may return 'ask'; it shares the agent's stream/context.
//
// LAWS:
//   - READ-ONLY: the classifier only INSPECTS (toolCall, ws, policy); it never
//     mutates. The read-only tool family (read/grep/glob/list) is auto-allowed.
//   - DENY-BY-DEFAULT: a destructive/unknown tool with no allow rule → 'block'
//     (the smoke "delete blocked before the tool call, no policy" relies on this).
//   - INTERMEDIATE: auto-review gates tool calls but is NEVER the final verdict —
//     honest-green stays final (enforced + tested in E4).

import {
  isReadOnlyTool,
  isDestructiveTool,
  AUTO_REVIEW_DECISIONS,
  validatePermissions,
} from './s4Contracts.js';

// Verb STEMS → tool-family map for natural-language rule matching (deterministic).
// Stems (not full words) so gerunds/plurals match: "writ" → write/writing/writes.
const ACTION_VERBS = {
  read_file: ['read', 'open', 'view', 'cat', 'show'],
  list_files: ['list', 'ls', 'dir'],
  grep_search: ['grep', 'search', 'find', 'ripgrep'],
  write_file: ['writ', 'creat', 'sav', 'new file', 'add file'],
  apply_edit: ['edit', 'modif', 'chang', 'patch', 'apply', 'replac'],
  run_command: [
    'run', 'command', 'execut', 'shell', 'bash', 'exec',
    'rm', 'delet', 'remov', 'destroy', 'instal', 'npm', 'npx', 'git', 'curl', 'sudo',
  ],
};

function safeJson(v) {
  try { return JSON.stringify(v ?? {}); } catch { return ''; }
}

/**
 * Structured block rule (tool-schema `block:[{tool,match}]`) matcher — EXACT.
 * Só casa quando o nome da tool bate E (sem match | args contêm match). Diferente do
 * ruleMatches NL: NÃO super-bloqueia (a regra "rm -rf" não pega todo run_command via verbo).
 * Estas regras SEMPRE vencem — inclusive sob auto-approve (WS-D / INV-5).
 * @param {{tool:string, match?:string}} rule @param {{name?:string, arguments?:object}} toolCall
 * @returns {boolean}
 */
function structuredBlockMatches(rule, toolCall) {
  if (!rule || typeof rule.tool !== 'string') return false;
  if (String(toolCall?.name ?? '') !== rule.tool) return false;
  if (!rule.match) return true; // block da tool inteira
  return safeJson(toolCall?.arguments).toLowerCase().includes(String(rule.match).toLowerCase());
}

/**
 * Does a natural-language rule match this tool call? Deterministic:
 *   - the rule names the tool (substring), OR
 *   - a verb in the rule maps to the tool's family, OR
 *   - a path/arg token in the rule appears in the tool's arguments.
 * @param {string} rule @param {{name?:string, arguments?:object}} toolCall
 * @returns {boolean}
 */
export function ruleMatches(rule, toolCall) {
  const r = String(rule ?? '').toLowerCase().trim();
  if (!r) return false;
  const name = String(toolCall?.name ?? '').toLowerCase();
  if (name && r.includes(name)) return true;

  const verbs = ACTION_VERBS[name] || [];
  if (verbs.some((v) => r.includes(v))) return true;

  const argText = safeJson(toolCall?.arguments).toLowerCase();
  const pathTokens = r
    .split(/[\s,]+/)
    .map((t) => t.replace(/^["'`]+|["'`.;:]+$/g, ''))
    .filter((t) => (t.includes('/') || t.includes('.')) && t.length >= 2);
  if (pathTokens.some((t) => argText.includes(t))) return true;

  return false;
}

function normalizeVerdict(v) {
  if (!v) return null;
  if (typeof v === 'string') {
    return AUTO_REVIEW_DECISIONS.includes(v) ? { decision: v, reason: 'classifier' } : null;
  }
  if (typeof v === 'object' && AUTO_REVIEW_DECISIONS.includes(v.decision)) {
    return { decision: v.decision, reason: typeof v.reason === 'string' ? v.reason : 'classifier' };
  }
  return null;
}

/**
 * Classify a tool call BEFORE execution. The deterministic steps (1-4, 6) resolve
 * synchronously; the OPTIONAL step-5 classifier (opts.classify) may be async (an LLM),
 * so the function is async — with no classifier it never awaits (resolves immediately,
 * behaviorally identical to the old sync path).
 *
 * @param {{ name?: string, arguments?: object }} toolCall
 * @param {object} [ws]                     workspace/context (read-only; never mutated)
 * @param {import('./s4Contracts.js').Permissions|null} [policy]  parsed .apollo/permissions.json
 * @param {{ classify?: (tc:object, ws:object, perm:object) => any }} [opts]  optional small-model hook
 * @returns {Promise<import('./s4Contracts.js').AutoReviewResult>}  { decision:'allow'|'block'|'ask', reason }
 */
export async function autoReview(toolCall, ws = {}, policy = null, opts = {}) {
  const name = toolCall?.name;

  // Normalize policy: malformed/absent → deny-by-default DEFAULT. Entende os DOIS
  // schemas (NL renderer + tool-schema canônico do Worker) — ver validatePermissions.
  const validated = validatePermissions(policy).value;
  const autoRun = validated.autoRun;
  const blockRules = Array.isArray(validated.blockRules) ? validated.blockRules : [];
  const askTools = Array.isArray(validated.askTools) ? validated.askTools : [];
  // default_deny === false (tool-schema) → gate final vira 'ask' (decisão humana) em vez
  // de hard-block. undefined (NL/legado/malformado) → deny-by-default (LEI preservada).
  const denyByDefault = validated.default_deny !== false;

  // 1. Structured block rules (tool-schema) — EXATO (tool+match). SEMPRE vencem: precedem
  //    ATÉ o auto-allow read-only (uma block rule contra read_file:.env TEM que bloquear;
  //    antes o early-return read-only da etapa 2 as ignorava — review WS-A/D #1).
  const structBlock = blockRules.find((rule) => structuredBlockMatches(rule, toolCall));
  if (structBlock) {
    return {
      decision: 'block',
      reason: structBlock.reason
        || `blocked by permissions rule: ${structBlock.tool}${structBlock.match ? ` ("${structBlock.match}")` : ''}`,
    };
  }

  // 2. Read-only family → allow (a não ser que uma block rule estruturada acima já vencesse).
  if (isReadOnlyTool(name)) {
    return { decision: 'allow', reason: `read-only tool "${name}" — no mutation` };
  }

  // 3. Explicit NL block rule wins (even over an allow rule — safe on conflict).
  const blockHit = autoRun.block_instructions.find((rule) => ruleMatches(rule, toolCall));
  if (blockHit) {
    return { decision: 'block', reason: `blocked by permissions rule: "${blockHit}"` };
  }

  // 4. Explicit allow rule → auto-run.
  const allowHit = autoRun.allow_instructions.find((rule) => ruleMatches(rule, toolCall));
  if (allowHit) {
    return { decision: 'allow', reason: `allowed by permissions rule: "${allowHit}"` };
  }

  // 5. Optional small-model classifier (SUBORDINATE) for ambiguous cases — awaited if async.
  //    A null verdict (timeout/error/unparseable/absent) falls through to the deterministic
  //    default below. This step is AFTER structured block + read-only + NL-block + allow, so a
  //    classifier can never overturn a hard rule; and its 'allow' still passes the runTool guards.
  if (typeof opts.classify === 'function') {
    const v = normalizeVerdict(await opts.classify(toolCall, ws, { autoRun }));
    if (v) return v;
  }

  // 6. Sem allow rule para tool destrutiva/desconhecida:
  //    - tool na lista `ask` da tool-schema → ASK, MESMO com default_deny:true (canônico
  //      block > ask > allow > default; antes a lista ask era descartada — review #2);
  //    - default_deny !== false → BLOCK (deny-by-default, LEI legada preservada);
  //    - default_deny === false → ASK (policy declara "não negue por padrão").
  const label = isDestructiveTool(name) ? `destructive tool "${name}"` : `tool "${name}"`;
  if (denyByDefault && !askTools.includes(name)) {
    return {
      decision: 'block',
      reason: `deny-by-default: ${label} has no allow rule in .apollo/permissions.json`,
    };
  }
  return {
    decision: 'ask',
    reason: askTools.includes(name)
      ? `ask: "${name}" — regra ask da policy (decisão do humano/permissionsMode)`
      : `ask: ${label} sem allow rule — policy default_deny=false → decisão do humano/permissionsMode`,
  };
}
